/* eslint-disable no-console, no-use-before-define */
/* global axios */
import Cookies from 'js-cookie';
import NotificationSubscriptions from '../api/notificationSubscription';
import { useAlert } from 'dashboard/composables';

const PREFERENCES_KEY = 'delfos_notification_preferences_v1';
const DEVICE_ID_KEY = 'delfos_notification_device_id_v1';
const PROTOCOL_VERSION = 1;
const INTERNAL_GROUPS_DEFAULT_VERSION = 1;
const AUTO_REGISTRATION_WATCH_MS = 2000;
const AUTO_REGISTRATION_FAILURE_RETRY_MS = 15 * 1000;
const AUTO_REGISTRATION_RECONCILE_MS = 15 * 60 * 1000;
const AUTO_REGISTRATION_LOGOUT_GUARD_MS = 30 * 1000;

let autoRegistrationWatcher = null;
let autoRegistrationInFlight = null;
let autoRegistrationReady = false;
let autoRegistrationLastAttemptAt = 0;
let autoRegistrationLastSuccessAt = 0;
let autoRegistrationSuspendedAt = 0;

const defaultPreferences = () => ({
  enabled: false,
  soundEnabled: true,
  previewEnabled: true,
  internalEnabled: true,
  internalGroupsDefaultVersion: INTERNAL_GROUPS_DEFAULT_VERSION,
  mutedInternalChannelIds: [],
  mutedUntil: null,
  deviceName: `${navigator.platform || 'Windows'} - ${
    navigator.userAgent.includes('Edg/') ? 'Edge' : 'Chrome'
  }`,
});

const hasAuthCookie = () => !!Cookies.get('cw_d_session_info');

const readPreferences = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(PREFERENCES_KEY) || '{}');
    const preferences = {
      ...defaultPreferences(),
      ...stored,
    };
    if (
      Number(stored.internalGroupsDefaultVersion || 0) <
      INTERNAL_GROUPS_DEFAULT_VERSION
    ) {
      preferences.internalEnabled = true;
      preferences.internalGroupsDefaultVersion =
        INTERNAL_GROUPS_DEFAULT_VERSION;
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
    }
    return preferences;
  } catch (error) {
    return defaultPreferences();
  }
};

export const getDelfosNotificationPreferences = () => readPreferences();

export const saveDelfosNotificationPreferences = preferences => {
  const normalized = { ...readPreferences(), ...preferences };
  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(normalized));
  window.dispatchEvent(
    new CustomEvent('delfos:notification-preferences-updated', {
      detail: normalized,
    })
  );
  return normalized;
};

export const getDelfosDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId =
      window.crypto?.randomUUID?.() ||
      `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return Promise.resolve(null);
  }

  return navigator.serviceWorker
    .register('/sw.js')
    .then(registration => {
      callback(registration);
      return registration;
    })
    .catch(registrationError => {
      console.error('SW registration failed:', registrationError);
      return null;
    });
};

export const hasPushPermissions = () =>
  'Notification' in window && Notification.permission === 'granted';

const generateKeys = value =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(value)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => {
  const preferences = readPreferences();
  return {
    subscription_type: 'browser_push',
    subscription_attributes: {
      endpoint: subscription.endpoint,
      p256dh: generateKeys(subscription.getKey('p256dh')),
      auth: generateKeys(subscription.getKey('auth')),
      device_id: getDelfosDeviceId(),
      device_name: preferences.deviceName,
      delfos_enabled: preferences.enabled,
      delfos_sound_enabled: preferences.soundEnabled,
      delfos_preview_enabled: preferences.previewEnabled,
      delfos_internal_enabled: preferences.internalEnabled,
      delfos_internal_default_version: preferences.internalGroupsDefaultVersion,
      delfos_muted_internal_channel_ids: preferences.mutedInternalChannelIds,
      delfos_muted_until: preferences.mutedUntil,
      delfos_protocol: PROTOCOL_VERSION,
      last_seen_at: new Date().toISOString(),
    },
  };
};

export const sendRegistrationToServer = subscription => {
  if (!hasAuthCookie() || !subscription) return Promise.resolve(null);
  return NotificationSubscriptions.create(
    getPushSubscriptionPayload(subscription)
  );
};

const subscribe = async () => {
  if (!window.chatwootConfig?.vapidPublicKey) {
    throw new Error('VAPID public key is unavailable');
  }

  const registration = await verifyServiceWorkerExistence();
  if (!registration) throw new Error('Service Worker is unavailable');

  const currentSubscription = await registration.pushManager.getSubscription();
  const subscription =
    currentSubscription ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: window.chatwootConfig.vapidPublicKey,
    }));

  await sendRegistrationToServer(subscription);
  autoRegistrationReady = true;
  autoRegistrationLastSuccessAt = Date.now();
  autoRegistrationSuspendedAt = 0;
  return subscription;
};

export const registerSubscription = (onSuccess = () => {}) =>
  subscribe()
    .then(subscription => {
      onSuccess(subscription);
      return subscription;
    })
    .catch(error => {
      console.error('Push subscription registration failed:', error);
      useAlert('Este navegador nao conseguiu ativar as notificacoes.');
      throw error;
    });

export const requestPushPermissions = async ({ onSuccess = () => {} } = {}) => {
  if (!('Notification' in window)) {
    useAlert('Este navegador nao oferece notificacoes do Windows.');
    return false;
  }

  let permission = Notification.permission;
  if (permission !== 'granted' && permission !== 'denied') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return false;

  saveDelfosNotificationPreferences({ enabled: true });
  await registerSubscription(onSuccess);
  return true;
};

const ensureAutoRegistrationWatcher = () => {
  if (autoRegistrationWatcher) return;

  autoRegistrationWatcher = window.setInterval(() => {
    autoRegisterDelfosPushSubscription().catch(() => {});
  }, AUTO_REGISTRATION_WATCH_MS);
};

export const autoRegisterDelfosPushSubscription = async () => {
  ensureAutoRegistrationWatcher();
  const preferences = readPreferences();
  const authenticated = hasAuthCookie();

  if (!authenticated) {
    autoRegistrationReady = false;
    autoRegistrationSuspendedAt = 0;
    return null;
  }

  if (!preferences.enabled || !hasPushPermissions()) {
    autoRegistrationReady = false;
    return null;
  }

  if (
    autoRegistrationSuspendedAt &&
    Date.now() - autoRegistrationSuspendedAt < AUTO_REGISTRATION_LOGOUT_GUARD_MS
  ) {
    return null;
  }

  autoRegistrationSuspendedAt = 0;
  if (
    autoRegistrationReady &&
    Date.now() - autoRegistrationLastSuccessAt < AUTO_REGISTRATION_RECONCILE_MS
  ) {
    return null;
  }

  if (autoRegistrationInFlight) return autoRegistrationInFlight;
  if (
    !autoRegistrationReady &&
    Date.now() - autoRegistrationLastAttemptAt <
      AUTO_REGISTRATION_FAILURE_RETRY_MS
  ) {
    return null;
  }

  autoRegistrationLastAttemptAt = Date.now();
  autoRegistrationInFlight = subscribe().finally(() => {
    autoRegistrationInFlight = null;
  });
  return autoRegistrationInFlight;
};

export const updateDelfosPushSubscription = async preferences => {
  const saved = saveDelfosNotificationPreferences(preferences);
  if (!saved.enabled) {
    await unregisterDelfosPushSubscription({ preservePreference: false });
    return null;
  }
  if (!hasPushPermissions()) return null;
  return subscribe();
};

export const unregisterDelfosPushSubscription = async ({
  preservePreference = true,
} = {}) => {
  const registration = await verifyServiceWorkerExistence();
  const subscription = await registration?.pushManager?.getSubscription();
  const deviceId = getDelfosDeviceId();

  if (hasAuthCookie()) {
    try {
      await axios.delete('/api/v1/notification_subscriptions', {
        params: subscription?.endpoint
          ? { endpoint: subscription.endpoint }
          : { device_id: deviceId },
      });
    } catch (error) {
      console.warn('Server push subscription cleanup failed:', error);
    }
  }

  await subscription?.unsubscribe();
  autoRegistrationReady = false;
  autoRegistrationLastAttemptAt = 0;
  autoRegistrationLastSuccessAt = 0;
  autoRegistrationSuspendedAt = preservePreference ? Date.now() : 0;
  if (!preservePreference) {
    saveDelfosNotificationPreferences({ enabled: false, mutedUntil: null });
  }
  postDelfosMessageToServiceWorker({ type: 'DELFOS_LOGOUT' });
  return true;
};

export const postDelfosMessageToServiceWorker = async message => {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  const worker =
    navigator.serviceWorker.controller ||
    registration?.active ||
    registration?.waiting;
  worker?.postMessage({ ...message, protocol: PROTOCOL_VERSION });
};
